import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";

/**
 * GoalEditorModal — Edit daily macro/energy goals + weight + weekly target rate.
 *
 * iter366 additions:
 *   • `weight_kg` (current body weight)
 *   • `weekly_target_kg` (-1.0 to +1.0; negative = loss, positive = gain)
 *     Derives the daily kcal deficit/surplus (~7700 kcal per kg of body fat)
 *     and live-previews the resulting `calories` field. The user can still
 *     manually override the calorie value after auto-fill.
 */
export function GoalEditorModal({ open, onClose, editGoals, setEditGoals, onSave, saving, isDark, bg, baselineCalories }) {
  if (!open || !editGoals) return null;

  const weightVal = editGoals.weight_kg ?? "";
  const weeklyVal = editGoals.weekly_target_kg ?? 0;

  const baseCals = Math.round(baselineCalories || editGoals.calories || 2000);
  const dailyDelta = Math.round((Number(weeklyVal) || 0) * 7700 / 7);
  const recommendedCals = Math.max(1200, baseCals + dailyDelta);

  // Cap at safe physiological rates (Hall 2016, NIH).
  const clampWeekly = (v) => Math.max(-1.0, Math.min(1.0, Number(v) || 0));

  const applyRecommendedCals = () => {
    setEditGoals(prev => ({ ...prev, calories: recommendedCals }));
  };

  const onWeeklyChange = (v) => {
    const w = clampWeekly(v);
    setEditGoals(prev => ({
      ...prev,
      weekly_target_kg: w,
      // Live-update calories so the trade-off is visible immediately.
      calories: Math.max(1200, baseCals + Math.round(w * 7700 / 7)),
    }));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="goal-editor-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md mx-4 max-h-[92vh] flex flex-col rounded-2xl ${isDark ? 'bg-[hsl(222,47%,11%)] border border-[hsl(223,47%,20%)]' : 'bg-white shadow-xl'}`}>
        <h3 className={`shrink-0 px-5 pt-5 pb-3 text-base font-bold border-b ${isDark ? 'text-white border-white/10' : 'text-[#1A1A2E] border-gray-100'}`}>
          {bg ? "Промени дневни цели" : "Edit Daily Goals"}
        </h3>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-3">

          {/* iter366 — Weight + weekly target section */}
          <div className={`mb-3 p-3 rounded-lg ${isDark ? 'bg-[hsl(223,47%,15%)] border border-[hsl(223,47%,25%)]' : 'bg-emerald-50 border border-emerald-100'}`}>
            <p className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
              {bg ? "Тегло и седмична цел" : "Weight & weekly target"}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {bg ? "Тегло (kg)" : "Weight (kg)"}
                </label>
                <Input
                  type="number"
                  value={weightVal}
                  placeholder="—"
                  onChange={e => setEditGoals(prev => ({ ...prev, weight_kg: e.target.value === "" ? null : parseFloat(e.target.value) }))}
                  step={0.1}
                  min={30}
                  max={250}
                  className={`h-10 ${isDark ? 'bg-[hsl(223,47%,18%)] border-[hsl(223,47%,22%)] text-white' : ''}`}
                  data-testid="goal-weight-input"
                />
              </div>
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {bg ? "Промяна (kg/седмица)" : "Change (kg/week)"}
                </label>
                <Input
                  type="number"
                  value={weeklyVal}
                  onChange={e => onWeeklyChange(e.target.value)}
                  step={0.1}
                  min={-1.0}
                  max={1.0}
                  className={`h-10 ${isDark ? 'bg-[hsl(223,47%,18%)] border-[hsl(223,47%,22%)] text-white' : ''}`}
                  data-testid="goal-weekly-target-input"
                />
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <p className={`text-[10px] leading-tight ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {bg
                  ? <>−1.0 до +1.0 kg/седмица (~{Math.abs(dailyDelta)} kcal/ден {dailyDelta < 0 ? "дефицит" : dailyDelta > 0 ? "излишък" : ""}).<br/>Препоръчителни калории: <strong className={isDark ? 'text-white' : 'text-gray-900'}>{recommendedCals} kcal</strong></>
                  : <>−1.0 to +1.0 kg/week (~{Math.abs(dailyDelta)} kcal/day {dailyDelta < 0 ? "deficit" : dailyDelta > 0 ? "surplus" : ""}).<br/>Recommended calories: <strong className={isDark ? 'text-white' : 'text-gray-900'}>{recommendedCals} kcal</strong></>}
              </p>
              <button
                type="button"
                onClick={applyRecommendedCals}
                className={`shrink-0 text-[10px] px-2 py-1 rounded-md font-semibold ${isDark ? 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}
                data-testid="goal-apply-recommended-cals"
              >
                {bg ? "Приложи" : "Apply"}
              </button>
            </div>
            {Math.abs(Number(weeklyVal)) > 0.75 && (
              <p className={`text-[10px] mt-2 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                {bg
                  ? "⚠ Темп над 0.75 kg/седмица може да доведе до загуба на мускулна маса и метаболитна адаптация (Hall 2016)."
                  : "⚠ Rate above 0.75 kg/week may cause muscle loss and metabolic adaptation (Hall 2016)."}
              </p>
            )}
          </div>

          {/* Macros section */}
          <p className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${isDark ? 'text-fuchsia-300' : 'text-fuchsia-600'}`}>
            {bg ? "Дневни макроси" : "Daily macros"}
          </p>
          <div className="space-y-3">
            {[
              { key: "calories", label: bg ? "Калории (kcal)" : "Calories (kcal)", step: 50 },
              { key: "protein", label: bg ? "Протеин (g)" : "Protein (g)", step: 5 },
              { key: "carbs", label: bg ? "Въглехидрати (g)" : "Carbs (g)", step: 5 },
              { key: "fat", label: bg ? "Мазнини (g)" : "Fat (g)", step: 5 },
            ].map(item => (
              <div key={item.key}>
                <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.label}</label>
                <Input
                  type="number"
                  value={editGoals[item.key]}
                  onChange={e => setEditGoals(prev => ({ ...prev, [item.key]: parseFloat(e.target.value) || 0 }))}
                  step={item.step}
                  className={`h-10 ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white' : ''}`}
                  data-testid={`goal-${item.key}-input`}
                />
              </div>
            ))}
          </div>
          <p className={`text-[10px] mt-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {bg
              ? "Тези стойности заменят AI препоръките до следващата оценка. Теглото и седмичната цел се записват и в профила (за DIL преизчисление)."
              : "These values override AI recommendations until next assessment. Weight and weekly target are also saved to the profile (for DIL recompute)."}
          </p>
        </div>
        <div className={`shrink-0 px-5 py-3 flex gap-2 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
          <Button variant="outline" onClick={onClose} className="flex-1 h-10 rounded-xl text-sm">
            {bg ? "Отказ" : "Cancel"}
          </Button>
          <Button onClick={onSave} disabled={saving} className="flex-1 h-10 gradient-primary text-white rounded-xl text-sm" data-testid="save-goals-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {bg ? "Запази" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
